import { environment } from '../../../environments/environment';

/**
 * Single source of truth for the Administration area: the hub cards, the in-section sub-nav, the
 * sidebar's "Administration" entry (shown when any item is visible) and the breadcrumb prefix.
 */
export interface AdminItem {
    label: string;
    icon: string;
    routerLink: string;
    description: string;
    /** Visible when the user holds any of these permissions. */
    permissions: string[];
    /** When false, the item is hidden (feature-flagged). Absent = shown. */
    enabled?: boolean;
}

export interface AdminGroup {
    label: string;
    items: AdminItem[];
}

export const ADMIN_GROUPS: readonly AdminGroup[] = [
    {
        label: 'People and access',
        items: [
            { label: 'Access management', icon: 'pi pi-users', routerLink: '/settings/access', description: 'Users, groups, roles and single sign-on.', permissions: ['user:manage', 'group:manage', 'role:manage', 'tenant:manage'] },
            { label: 'Audit log', icon: 'pi pi-history', routerLink: '/audit-log', description: 'Who changed what, and when.', permissions: ['audit:view'] }
        ]
    },
    {
        label: 'Process setup',
        items: [
            { label: 'Journey templates', icon: 'pi pi-arrow-right-arrow-left', routerLink: '/journey-templates', description: 'Stage order for each request type.', permissions: ['workflow:manage'] },
            { label: 'Workflows', icon: 'pi pi-directions', routerLink: '/workflows', description: 'States, transitions and approval rules.', permissions: ['workflow:manage'] },
            { label: 'Protocol templates', icon: 'pi pi-clone', routerLink: '/tp-templates', description: 'Protocol sections and extraction prompts.', permissions: ['protocol:view'] },
            { label: 'Tag management', icon: 'pi pi-tag', routerLink: '/settings/tags', description: 'Tag groups, scope, merge and archive.', permissions: ['tag:manage'] }
        ]
    },
    {
        label: 'Data and integration',
        items: [
            { label: 'Migration', icon: 'pi pi-upload', routerLink: '/migration', description: 'Import a customer’s existing content.', permissions: ['protocol:edit'] },
            { label: 'Integrations', icon: 'pi pi-share-alt', routerLink: '/integrations', description: 'EMR and external system connections.', permissions: ['integration:view'] },
            { label: 'Adapters', icon: 'pi pi-code', routerLink: '/adapters', description: 'Transformations and the CBCM schema.', permissions: ['adapter:view'] },
            { label: 'Site scope', icon: 'pi pi-building', routerLink: '/site-scope', description: 'Sites and what each one covers.', permissions: ['site-scope:view'] },
            { label: 'Terminology', icon: 'pi pi-book', routerLink: '/terminology', description: 'Code systems and value sets.', permissions: ['terminology:view'], enabled: false }
        ]
    },
    {
        label: 'Platform',
        items: [
            { label: 'AI gateway', icon: 'pi pi-microchip-ai', routerLink: '/settings/ai-gateway', description: 'Models, task routing and usage.', permissions: ['ai-gateway:view'] },
            { label: 'EMR sandbox', icon: 'pi pi-server', routerLink: '/settings/emr-sandbox', description: 'Internal FHIR test bench.', permissions: ['tenant:manage'], enabled: environment.emrSandbox }
        ]
    }
];

/** Path prefixes that belong to the Administration area (an item's route plus its sub-pages). */
const ADMIN_PREFIXES = ['/admin', '/settings', '/workflows', '/workflows-old', '/journey-templates', '/tp-templates', '/migration', '/integrations', '/adapters', '/site-scope', '/audit-log', '/terminology'];

export function isAdminPath(path: string): boolean {
    const p = path.split('?')[0].split('#')[0];
    return ADMIN_PREFIXES.some((x) => p === x || p.startsWith(x + '/'));
}

/** Groups with only the items this user may see; empty groups dropped. */
export function visibleAdminGroups(can: (perms: string[]) => boolean): AdminGroup[] {
    return ADMIN_GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => i.enabled !== false && can(i.permissions)) })).filter((g) => g.items.length > 0);
}
